// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import React, { FC } from "react";

// eslint-disable-next-line spire/fenced-imports
import { css } from "styled-components";

const enum fields {
    buttonLink = "buttonLink",
    buttonLinkText = "buttonLinkText",
}

export interface ServiceShowcaseStyles {
    buttonBreakpointStyles: InjectableCss;
}

export const serviceShowcaseStyles: ServiceShowcaseStyles = {
    buttonBreakpointStyles: {
        css: css`
            padding-left: 15px;
            padding-bottom: 15px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    css`
                        display: inline-flex;
                    `,
                    css`
                        display: inline-flex;
                    `,
                    css`
                        display: inline-flex;
                    `,
                ])}
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonLink]: string;
        [fields.buttonLinkText]: string;
    };
}

type Props = OwnProps & HasHistory;

const styles = serviceShowcaseStyles;
const ShowcaseButton: FC<Props> = ({ fields, history }) => {
    const routeToClick = () => {
        if (fields.buttonLink.includes("http")) {
            window.location.href = fields.buttonLink;
        } else {
            const scrollElement = document.getElementById(fields.buttonLink);
            scrollElement!.scrollIntoView();
        }
    };

    return (
        <StyledWrapper
            data-test-selector="custom_content_service_button"
            {...styles.buttonBreakpointStyles}
            className="custom_content_service_button homepageProductbuttons"
        >
            <Button
                size={24}
                shape="pill"
                typographyProps={{ color: `#1658ec`, size: 12, weight: 600 }}
                css={css`
                    background-color: #eff6ff;
                    background: #eff6ff;
                    border: 0.1em solid #1658ec;
                    padding: 5px 20px;
                    line-height: normal;
                    backface-visibility: visible;
                    text-align: left;
                `}
                hoverStyle={{
                    background: `#eff6ff`,
                    borderColor: `#eff6ff`,
                }}
                onClick={routeToClick}
                className="homepageProductbuttons"
            >
                {fields.buttonLinkText}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(ShowcaseButton),
    definition: {
        displayName: "Showcase Button",
        icon: "Grid",
        fieldDefinitions: [
            {
                name: fields.buttonLink,
                displayName: "Button link",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The button link URL.",
                placeholder: "",
                isRequired: true,
            },
            {
                name: fields.buttonLinkText,
                displayName: "Button link text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The button link text.",
                placeholder: "My link text",
                isRequired: true,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
